import axios from "axios";
import {
  AssetModalData,
  CollectionModalData,
  GroupModalData,
} from "./ChangeRequestModal";
import { OptionType } from "../../types/types";
import { endpoints } from "../../endpoints";
import { EntityType } from "../../types/types.MDM";
import { AttributeSchema } from "../../types/types.PIM";
import { getChangeRequestStatusColor, StatusLeft } from "../Status/Status";
import { IRow } from "../../pages/admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { CollectionAttributeDisplayValue } from "../../util/collectionAttributeDisplayValue";
import { collectionAttributeValueHasDisplayValue } from "../../pages/admin/SellerAdmin/MDM/shared/utils";
import { getAttributeValue } from "../../pages/admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import { fontSizeTokens } from "../../designTokens";

export type ChangeRequestStatusType = "new" | "accepted" | "discarded";

export type ChangeRequestContextType = "collection" | "group" | "asset";
export interface ChangeRequestFormValues {
  assignee: OptionType<string>;
  attributes: OptionType<string>[];
  request_details: string;
}

export type ChangeRequestVariantContext =
  | { variant: "collection"; collectionData: CollectionModalData }
  | { variant: "group"; groupData: GroupModalData }
  | { variant: "asset"; assetData: AssetModalData };

export async function submitChangeRequest({
  tenant_id,
  entity,
  context,
  formValues,
  isProduct = false,
  onSuccess,
}: {
  tenant_id: string;
  isProduct?: boolean;
  entity: { id: string; entity_type?: EntityType; name?: string };
  context: ChangeRequestVariantContext;
  formValues: ChangeRequestFormValues;
  onSuccess?: () => void;
}) {
  const payload = {
    ...resolveVariantFields(context),
    ...(context.variant === "asset"
      ? { asset_fields: formValues.attributes.map((a) => a.value) }
      : { attribute_ids: formValues.attributes.map((a) => a.value) }),
    assignee_id: formValues.assignee ? formValues.assignee.value : null,
    details: formValues.request_details,
  };

  await axios.post(
    isProduct
      ? endpoints.v2_tenants_id_pim_products_id_change_requests(
          tenant_id,
          entity.id
        )
      : endpoints.v2_tenants_id_entities_id_change_requests(
          tenant_id,
          (entity.entity_type + "s") as EntityType,
          entity.id
        ),
    payload
  );

  onSuccess?.();
}

function resolveVariantFields(context: ChangeRequestVariantContext) {
  switch (context.variant) {
    case "collection":
      return {
        context_type: "collection" as const,
        attribute_group_id:
          context.collectionData.collection.attribute_group_id,
        collection_id: context.collectionData.collection.id,
        collection_row_id: context.collectionData.rowId,
      };
    case "group":
      return {
        context_type: "group" as const,
        attribute_group_id: context.groupData.groupId,
      };
    case "asset":
      return {
        context_type: "asset" as const,
        asset_id: context.assetData.details?.id ?? null,
      };
  }
}

export function resolveAttributeOptions(
  attributes: AttributeSchema[]
): Array<{ label: string; value: string }> {
  return (attributes ?? []).map((attr) => ({
    label: (attr as AttributeSchema).display_name || attr.name,
    value: attr.id,
  }));
}

export function ChangeRequestStatus({
  status,
}: {
  status: ChangeRequestStatusType;
}) {
  const color = getChangeRequestStatusColor(
    (status as ChangeRequestStatusType) ?? "draft"
  );
  return (
    <StatusLeft
      color={color}
      text={status as ChangeRequestStatusType}
      textStyle={{ fontSize: fontSizeTokens.font_size_md }}
      productStatusType="default"
    />
  );
}

export function buildCollectionTableData(
  row: { row_id: string; values: any[] } | undefined,
  visibleColumnById?: Map<string, AttributeSchema>
): { column: AttributeSchema; value: CollectionAttributeDisplayValue }[] {
  if (!row) return [];

  const iRow: IRow = { uuid: row.row_id, edit: false, row_editable: true };

  const flatRow = row.values.reduce<IRow>(
    (acc, attrValue) => ({
      ...acc,
      [attrValue.attribute_id]: getAttributeValue(attrValue, acc),
    }),
    iRow
  );

  return row.values.reduce<
    { column: AttributeSchema; value: CollectionAttributeDisplayValue }[]
  >((acc, attrValue) => {
    if (acc.some(({ column }) => column.id === attrValue.attribute_id)) {
      return acc;
    }
    const column: AttributeSchema =
      visibleColumnById?.get(attrValue.attribute_id) ??
      ({
        id: attrValue.attribute_id,
        name: attrValue.attribute_id,
        display_name: attrValue.attribute_id,
        input_type: attrValue.data_type,
      } as AttributeSchema);

    if (!collectionAttributeValueHasDisplayValue(attrValue)) {
      return acc;
    }

    const value = flatRow[attrValue.attribute_id];
    if (value === undefined) return acc;
    acc.push({ column, value: value as CollectionAttributeDisplayValue });
    return acc;
  }, []);
}
